/**
 * Prefetch Accelerator v2 — intent-gated prefetch + PJAX soft navigation.
 *
 * Design record: handover/sprint-2026-08-prefetch-v2-intent-and-tracking/
 * (001 plan, 002 planreview, 003 accepted revisions). Key invariants:
 *
 *  - ONE delegated click listener on document. Eligibility: same-origin
 *    http(s) `a.product-link` whose canonical path is a product URL, primary
 *    button, no modifier keys, no target/download, not `.quickbuy-toggle`.
 *    Collection-card links deliberately fall through to native navigation.
 *  - ONE request registry keyed by canonical product path (collection-prefixed
 *    aliases share identity). Max 3 concurrent background prefetches (1 in
 *    intent-only mode when the connection estimator reports 2G — the estimator
 *    is advisory; saveData is the only hard gate). Intent prefetch is live from
 *    boot; proximity scanning starts at load+idle or an 8 s cap.
 *  - v2.1 (2026-08-10): a click soft-navigates ONLY on a fresh cache hit.
 *    Uncached clicks — including clicks on a document still in flight — are
 *    never preventDefault'ed; they stay native navigations with the browser's
 *    own loading feedback. The v2.0 foreground wait (intercept every click,
 *    await the network with no timeout, no loading UI, connection gates
 *    bypassed) froze taps for seconds and was the prime suspect in the
 *    10 Aug CVR drop. A soft nav that somehow exceeds
 *    FOREGROUND_NAV_BUDGET_MS falls back to hard navigation.
 *  - ONE bounded cache writer (30 unique documents, oldest-inserted eviction).
 *    `window.prefetchCache` keeps its object identity; product-preview-panel.js
 *    reads it and registers links via window.PrefetchAccel.instance.
 *  - Pre-commit failures (fetch, parse, validate, swap) hard-navigate — a
 *    broken accelerator must degrade to a working link, never a dead click.
 *    Post-commit integration failures are isolated and never navigation-fatal.
 *  - Section-owned scripts: DOMParser marks scripts non-executable, and that
 *    flag survives insertion, so `<script src>` tags inside swapped-in content
 *    never run on their own. Post-commit, any script[src] URL the document has
 *    not executed yet is re-created as a live script tag (once per URL) so
 *    sections absent from the origin page — e.g. scrolling-banner.js on a PDP
 *    entered from a collection — get their behaviour after a soft nav. Only
 *    shapes the browser WOULD execute are recorded or cloned (non-JS types
 *    and modern-browser nomodule are skipped without poisoning the URL —
 *    audit 004 blocker); the clone copies every authored attribute except id
 *    (type, nomodule, integrity, crossorigin, referrerpolicy, nonce, ...) so
 *    execution happens under hard-load constraints (audit 002 blocker).
 *  - Soft-nav tracking: theme JS is the single analytics owner (there is no
 *    GTM container — Mat, 2026-08-08). GA4 page_view / Meta PageView /
 *    Hotjar stateChange fire exactly once per committed navigation.
 *    Shopify's built-in Web Pixels reports CANNOT be fed from theme JS —
 *    `Shopify.analytics.publish` only reaches custom pixels, so Shopify
 *    session/funnel reports undercount soft navs. Do not try to "fix" this
 *    by publishing `page_viewed`; it is not possible from here.
 *  - Never runs inside the Shopify theme editor. The theme.liquid include is
 *    guarded with request.design_mode; the designMode check below is a
 *    backstop (see AI DOCS/Theme-Editor-Failed-To-Load-Prefetch-Investigation.md).
 *
 * Test seam: window.PrefetchAccel.create(deps) builds an instance against
 * injectable {win, doc, fetchFn, hardNavigate, reloadPage, now}. The Node
 * harnesses in tests/prefetch-accelerator-*.test.js drive the real handlers
 * through this factory. `_internals` is exposed for tests only — not an API.
 */
(function () {
  'use strict';

  var MAX_CONCURRENT_PREFETCHES = 3;
  var MAX_CACHED_DOCUMENTS = 30;
  var VIEWPORT_LOOKAHEAD = '450px';
  var DEFAULT_CACHE_MINUTES = 10;
  // Heavy pages can hold the load event back for tens of seconds; proximity
  // acquisition starts at load+idle or this cap, whichever comes first.
  var PROXIMITY_START_CAP_MS = 8000;
  // deferredLinks holds strong node references; bound it so a long
  // restricted-mode session cannot accumulate detached DOM (014 finding).
  var MAX_DEFERRED_LINKS = 40;
  // v2.1: a click-triggered soft nav should be a same-tick cache read + DOM
  // swap. If it has not committed inside this budget, something slow slipped
  // through (cache-expiry race, future regression) — give the user a real
  // navigation instead of a frozen page.
  var FOREGROUND_NAV_BUDGET_MS = 800;

  function createAccelerator(deps) {
    var win = deps.win;
    var doc = deps.doc;
    var fetchFn = deps.fetchFn;
    var hardNavigate = deps.hardNavigate;
    var reloadPage = deps.reloadPage;
    var now = deps.now || function () { return Date.now(); };

    var config = {
      // Kill switch for soft-nav page-view emission (003 — operational
      // insurance only; there is no GTM container to conflict with).
      softNavTracking: true
    };

    // ---- state -----------------------------------------------------------
    var cacheStore = {};        // path -> {html, timestamp}; aliases share entries
    var cacheKeyOrder = [];     // canonical keys, insertion order (eviction)
    var cacheAliases = {};      // canonical -> [alias paths]
    var registry = {};          // canonical -> {status, url, aliasPath, promise, settled}
    var queue = [];             // canonical paths awaiting a slot
    var inflightCount = 0;
    var navToken = 0;
    var currentVirtualUrl = win.location.href;
    var acquisitionStarted = false;
    var registeredLinks = typeof WeakSet === 'function' ? new WeakSet() : null;
    var pendingRoots = [];
    var deferredLinks = [];     // intersected links rejected by a temporary mode
    var waitingForVisibility = false;
    var io = null;
    var executedScriptSrcs = {};  // absolute script URL -> true once executed

    function log(label, detail) {
      if (win.DBLogger && typeof win.DBLogger.warn === 'function') {
        win.DBLogger.warn('[prefetch-accel] ' + label, detail);
      }
    }

    function debug(label) {
      if (win.DBLogger && typeof win.DBLogger.log === 'function') {
        win.DBLogger.log('[prefetch-accel] ' + label);
      }
    }

    function safeRun(label, fn) {
      try { fn(); } catch (err) { log(label + ' failed', err); }
    }

    // ---- url helpers -----------------------------------------------------
    function getPathWithoutQuery(url) {
      try {
        var parsed = new URL(String(url), win.location.origin);
        var path = parsed.pathname;
        var pageParam = parsed.searchParams.get('page');
        if (pageParam) path += '?page=' + pageParam;
        return path;
      } catch (err) {
        return null;
      }
    }

    function getPrimaryUrl(url) {
      try {
        url = String(url);
        if (url.indexOf('/collections/') !== -1 && url.indexOf('/products/') !== -1) {
          return url.replace(/\/collections\/[^/]+\//, '/');
        }
        return null;
      } catch (err) {
        return null;
      }
    }

    // Canonical identity: the collection-stripped product path. The clicked
    // (possibly collection-prefixed) path is kept as an alias so history and
    // preview-panel reads keep working.
    function canonicalize(url) {
      var path = getPathWithoutQuery(url);
      if (!path) return null;
      var primary = getPrimaryUrl(url);
      var canonical = primary ? getPathWithoutQuery(primary) : path;
      if (!canonical) return null;
      return {
        canonical: canonical,
        aliasPath: canonical === path ? null : path
      };
    }

    function isProductPath(path) {
      return typeof path === 'string' && path.indexOf('/products/') !== -1;
    }

    /**
     * saveData is the ONLY hard gate — it is an explicit user preference.
     * effectiveType is the browser's RTT-based guess and is advisory only:
     * it routinely misreports fast links as 2G (observed on 389 Mbps wifi in
     * Edge during the browser gate), so a 2G estimate degrades to intent-only
     * prefetching with concurrency 1 rather than disabling the feature.
     * Re-evaluated per call, so a recovering estimate restores full mode.
     */
    function acquisitionMode() {
      var conn = win.navigator && win.navigator.connection;
      if (conn && conn.saveData === true) return 'off';
      if (conn && typeof conn.effectiveType === 'string' && /(^|-)2g$/.test(conn.effectiveType)) return 'intent-only';
      return 'full';
    }

    function currentConcurrencyCap() {
      var mode = acquisitionMode();
      if (mode === 'off') return 0;
      if (mode === 'intent-only') return 1;
      return MAX_CONCURRENT_PREFETCHES;
    }

    // ---- bounded cache (single writer) -----------------------------------
    function cacheTtlMs() {
      var minutes = win.theme && win.theme.settings && win.theme.settings.prefetchCachePeriod;
      return (typeof minutes === 'number' && minutes > 0 ? minutes : DEFAULT_CACHE_MINUTES) * 60000;
    }

    /**
     * The ONLY place cache entries are created. `aliases` may be null, a
     * string, or an array. Every alias ever recorded for a canonical is
     * repointed at the new entry object on rewrite, so one canonical never
     * retains more than one decoded document regardless of how many
     * collection-prefixed aliases it accumulates (005 blocker 3).
     */
    function cacheWrite(canonical, aliases, html) {
      var entry = { html: html, timestamp: now() };
      if (!(canonical in cacheStore)) cacheKeyOrder.push(canonical);
      cacheStore[canonical] = entry;

      var known = cacheAliases[canonical] || (cacheAliases[canonical] = []);
      var incoming = aliases == null ? [] : (typeof aliases === 'string' ? [aliases] : aliases);
      for (var a = 0; a < incoming.length; a++) {
        var alias = incoming[a];
        if (alias && alias !== canonical && known.indexOf(alias) === -1) known.push(alias);
      }
      for (var k = 0; k < known.length; k++) cacheStore[known[k]] = entry;

      while (cacheKeyOrder.length > MAX_CACHED_DOCUMENTS) {
        var evicted = cacheKeyOrder.shift();
        debug('evicted oldest cached doc: ' + evicted);
        delete cacheStore[evicted];
        var evictedAliases = cacheAliases[evicted];
        if (evictedAliases) {
          for (var i = 0; i < evictedAliases.length; i++) delete cacheStore[evictedAliases[i]];
          delete cacheAliases[evicted];
        }
      }
    }

    /**
     * Record an alias observed AFTER its canonical entry already exists:
     * point the alias key at the existing entry without creating a document
     * or refreshing its timestamp (007 finding 1).
     */
    function cacheAddAlias(canonical, aliasPath) {
      if (!aliasPath || aliasPath === canonical) return;
      var entry = cacheStore[canonical];
      if (!entry) return;
      var known = cacheAliases[canonical] || (cacheAliases[canonical] = []);
      if (known.indexOf(aliasPath) === -1) known.push(aliasPath);
      cacheStore[aliasPath] = entry;
    }

    function readFresh(path) {
      if (!path) return null;
      var entry = cacheStore[path];
      if (!entry) return null;
      if (now() - entry.timestamp >= cacheTtlMs()) return null;
      return entry;
    }

    function snapshotCurrentPage() {
      var path = getPathWithoutQuery(win.location.href);
      if (!path) return;
      cacheWrite(path, null, doc.documentElement.outerHTML);
    }

    // ---- request registry ------------------------------------------------
    function fetchDocument(canonical, url) {
      var rec = registry[canonical];
      var settle = function () {
        if (rec.settled) return;
        rec.settled = true;
        if (rec.status === 'inflight') inflightCount--;
        delete registry[canonical];
        pump();
      };
      rec.promise = fetchFn(url).then(function (response) {
        if (!response || !response.ok) {
          throw new Error('HTTP ' + (response ? response.status : 'no response'));
        }
        return response.text();
      }).then(function (html) {
        try {
          // rec.aliases is read at completion time, so aliases merged in by
          // later joiners (awaitDocument / schedulePrefetch) are included.
          cacheWrite(canonical, rec.aliases, html);
          debug((rec.status === 'foreground' ? 'fetched and cached: ' : 'prefetched and cached: ') + canonical + ' (cache: ' + cacheKeyOrder.length + ' docs)');
        } finally {
          settle();
        }
        return html;
      }, function (err) {
        settle();
        throw err;
      });
      return rec.promise;
    }

    function pump() {
      while (inflightCount < currentConcurrencyCap() && queue.length > 0) {
        var canonical = queue.shift();
        var rec = registry[canonical];
        if (!rec || rec.status !== 'queued') continue;
        rec.status = 'inflight';
        inflightCount++;
        debug('prefetch started: ' + canonical + ' (' + inflightCount + ' in flight)');
        fetchDocument(canonical, rec.url).catch(function () {
          // Background prefetch failure is not an error condition.
        });
      }
    }

    /**
     * Returns false when the work was rejected by the CURRENT mode or start
     * state (retry-later — callers may defer it); true when it was handled:
     * queued, joined, already fresh, or permanently ineligible.
     */
    function schedulePrefetch(url, intent) {
      // Intent (hover/touch/focus) is a user signal and is honoured from boot;
      // proximity scanning waits for startAcquisition (load/idle or the cap).
      if (!intent && !acquisitionStarted) return false;
      var mode = acquisitionMode();
      if (mode === 'off') return false;
      if (mode === 'intent-only' && !intent) return false;
      var ids = canonicalize(url);
      if (!ids || !isProductPath(ids.canonical)) return true;
      if (readFresh(ids.canonical)) {
        cacheAddAlias(ids.canonical, ids.aliasPath);
        return true;
      }
      var rec = registry[ids.canonical];
      if (rec) {
        mergeAlias(rec, ids.aliasPath);
        if (intent && rec.status === 'queued') {
          var at = queue.indexOf(ids.canonical);
          if (at > 0) {
            queue.splice(at, 1);
            queue.unshift(ids.canonical);
          }
        }
        // Wake a queue stalled by an earlier restrictive mode — without this,
        // intent on an existing record never re-pumps (012 finding 1).
        pump();
        return true;
      }
      registry[ids.canonical] = {
        status: 'queued',
        url: url,
        aliases: ids.aliasPath ? [ids.aliasPath] : [],
        settled: false,
        promise: null
      };
      if (intent) queue.unshift(ids.canonical); else queue.push(ids.canonical);
      debug('prefetch queued (' + (intent ? 'intent' : 'proximity') + '): ' + ids.canonical);
      pump();
      return true;
    }

    /**
     * NetworkInformation change wake-up (012 finding 1): re-pump any stalled
     * queue and re-arm proximity links that a temporary off/intent-only mode
     * rejected after their observer had already released them.
     */
    function onConnectionChange() {
      safeRun('connection change wake-up', function () {
        pump();
        if (deferredLinks.length === 0) return;
        if (acquisitionMode() !== 'full') return;
        var links = deferredLinks.splice(0, deferredLinks.length);
        for (var i = 0; i < links.length; i++) {
          // Nodes removed by PJAX swaps or AJAX filtering are stale — do not
          // fetch for them and do not retain them (014 finding). Absence of
          // the isConnected API keeps the link (never treat unknown as stale).
          if (links[i].isConnected === false) continue;
          if (schedulePrefetch(links[i].href, false) === false) deferredLinks.push(links[i]);
        }
      });
    }

    function mergeAlias(rec, aliasPath) {
      if (aliasPath && rec.aliases.indexOf(aliasPath) === -1) rec.aliases.push(aliasPath);
    }

    // Foreground: the user asked for this document. Since v2.1 the click
    // handler only calls softNavigate on a fresh cache hit, so from clicks
    // this resolves same-tick from cache; the join/fetch paths below remain
    // for popstate restores (where native fallthrough is not an option) and
    // are bounded by their callers' budget timers.
    function awaitDocument(url) {
      var ids = canonicalize(url);
      if (!ids) return Promise.reject(new Error('uncanonicalizable url'));
      var fresh = readFresh(ids.canonical);
      if (fresh) {
        cacheAddAlias(ids.canonical, ids.aliasPath);
        debug('navigate: CACHE HIT ' + ids.canonical + ' (age ' + Math.round((now() - fresh.timestamp) / 1000) + 's)');
        return Promise.resolve(fresh.html);
      }
      var aliasFresh = ids.aliasPath && readFresh(ids.aliasPath);
      if (aliasFresh) {
        debug('navigate: CACHE HIT (alias) ' + ids.aliasPath);
        return Promise.resolve(aliasFresh.html);
      }
      var rec = registry[ids.canonical];
      if (rec && rec.promise) {
        // Joining an in-flight request: record the newly observed alias so
        // the completion write creates its cache key too (005 blocker 3).
        mergeAlias(rec, ids.aliasPath);
        debug('navigate: joining in-flight prefetch ' + ids.canonical);
        return rec.promise;
      }
      if (rec && rec.status === 'queued') {
        mergeAlias(rec, ids.aliasPath);
        var at = queue.indexOf(ids.canonical);
        if (at !== -1) queue.splice(at, 1);
        rec.status = 'foreground';
        debug('navigate: promoting queued prefetch to foreground fetch ' + ids.canonical);
        return fetchDocument(ids.canonical, rec.url);
      }
      debug('navigate: NOT CACHED, network fetch ' + ids.canonical);
      registry[ids.canonical] = {
        status: 'foreground',
        url: url,
        aliases: ids.aliasPath ? [ids.aliasPath] : [],
        settled: false,
        promise: null
      };
      return fetchDocument(ids.canonical, url);
    }

    // ---- navigation ------------------------------------------------------
    function isEligibleClick(event, anchor) {
      if (event.defaultPrevented) return false;
      if (typeof event.button === 'number' && event.button !== 0) return false;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
      if (!anchor || !anchor.href) return false;
      if (anchor.classList && anchor.classList.contains('quickbuy-toggle')) return false;
      if (anchor.hasAttribute && anchor.hasAttribute('download')) return false;
      // 003 rule: the PRESENCE of any authored target attribute opts out —
      // including target="_self" and the empty target="" form, which
      // getAttribute would report as falsy (007 finding 2).
      if (anchor.hasAttribute && anchor.hasAttribute('target')) return false;
      var parsed;
      try {
        parsed = new URL(anchor.href, win.location.href);
      } catch (err) {
        return false;
      }
      if (parsed.origin !== win.location.origin) return false;
      if (!/^https?:$/.test(parsed.protocol)) return false;
      return true;
    }

    function onDocumentClick(event) {
      var anchor = null;
      try {
        var target = event.target;
        anchor = target && typeof target.closest === 'function' ? target.closest('a.product-link') : null;
        if (!anchor || !isEligibleClick(event, anchor)) return;
        var ids = canonicalize(anchor.href);
        if (!ids || !isProductPath(ids.canonical)) return;
      } catch (err) {
        // Guard-path failure: do nothing, native navigation proceeds.
        log('click eligibility check failed', err);
        return;
      }
      // v2.1 gate: intercept ONLY when the document is already cached fresh.
      // No cache -> no preventDefault -> the browser navigates natively with
      // its own progress feedback. This must be decided synchronously, before
      // preventDefault, so a slow path can never hold a frozen page.
      var fresh;
      try {
        fresh = readFresh(ids.canonical) || (ids.aliasPath ? readFresh(ids.aliasPath) : null);
      } catch (err) {
        log('cache gate check failed', err);
        return;
      }
      if (!fresh) {
        debug('click: not cached, native navigation ' + ids.canonical);
        return;
      }
      event.preventDefault();
      // Single freshness decision (v2.1 audit 002 blocker 1): the entry the
      // gate accepted IS the navigation input. A second readFresh inside the
      // navigation could disagree at the TTL boundary and reopen the
      // foreground network path this gate exists to close.
      softNavigate(anchor.href, ids, fresh);
    }

    function softNavigate(url, ids, freshEntry) {
      var token = ++navToken;
      var started = now();
      var historyPath = ids.aliasPath || ids.canonical;
      if (freshEntry) {
        // Gate-supplied entry: commit synchronously from it — no re-read, no
        // timer, no async gap. Expiry between the gate's read and this commit
        // cannot re-decide the navigation (002 blocker 1); a stale-but-just-
        // accepted document is the correct payload for the click that won it.
        try {
          cacheAddAlias(ids.canonical, ids.aliasPath);
          commitNavigation(freshEntry.html, historyPath, { push: true });
          debug('soft nav committed: ' + historyPath + ' (' + Math.round(now() - started) + 'ms after click, gate-supplied cache hit)');
        } catch (err) {
          log('commit failed, falling back to hard navigation', err);
          hardNavigate(url);
        }
        return;
      }
      // No gate-supplied entry (not reachable from clicks; kept for direct
      // callers): bounded async path.
      // v2.1 budget: this timer exists so no code path — present or future —
      // can hold a navigation frozen past the budget.
      var settled = false;
      var budgetTimer = win.setTimeout(function () {
        if (settled || token !== navToken) return;
        settled = true;
        log('foreground budget exceeded, falling back to hard navigation', url);
        hardNavigate(url);
      }, FOREGROUND_NAV_BUDGET_MS);
      try {
        awaitDocument(url).then(function (html) {
          if (settled || token !== navToken) { win.clearTimeout(budgetTimer); return; }
          settled = true;
          win.clearTimeout(budgetTimer);
          try {
            commitNavigation(html, historyPath, { push: true });
            debug('soft nav committed: ' + historyPath + ' (' + Math.round(now() - started) + 'ms after click)');
          } catch (err) {
            log('commit failed, falling back to hard navigation', err);
            hardNavigate(url);
          }
        }, function (err) {
          if (settled || token !== navToken) { win.clearTimeout(budgetTimer); return; }
          settled = true;
          win.clearTimeout(budgetTimer);
          log('foreground fetch failed, falling back to hard navigation', err);
          hardNavigate(url);
        });
      } catch (err) {
        settled = true;
        win.clearTimeout(budgetTimer);
        log('soft navigation failed, falling back to hard navigation', err);
        hardNavigate(url);
      }
    }

    function onPopstate() {
      var token = ++navToken;
      try {
        var path = getPathWithoutQuery(win.location.href);
        var entry = readFresh(path);
        if (entry) {
          debug('history restore: CACHE HIT ' + path);
          commitNavigation(entry.html, path, {});
          return;
        }
        // Cache miss: go through the request registry like every other
        // navigation, so a popstate can join an in-flight prefetch instead
        // of issuing a duplicate request whose late response could overwrite
        // a newer cache entry (005 blocker 2). Native fallthrough is not an
        // option here — the URL has already changed — so the v2.1 budget
        // degrades to a reload, which gives the browser's own loading UX.
        var settled = false;
        var budgetTimer = win.setTimeout(function () {
          if (settled || token !== navToken) return;
          settled = true;
          log('popstate budget exceeded, reloading', path);
          reloadPage();
        }, FOREGROUND_NAV_BUDGET_MS);
        awaitDocument(win.location.href).then(function (html) {
          if (settled || token !== navToken) { win.clearTimeout(budgetTimer); return; }
          settled = true;
          win.clearTimeout(budgetTimer);
          try {
            commitNavigation(html, path, {});
          } catch (err) {
            log('popstate commit failed, reloading', err);
            reloadPage();
          }
        }, function (err) {
          if (settled || token !== navToken) { win.clearTimeout(budgetTimer); return; }
          settled = true;
          win.clearTimeout(budgetTimer);
          log('popstate restore failed, reloading', err);
          reloadPage();
        });
      } catch (err) {
        log('popstate handling failed, reloading', err);
        reloadPage();
      }
    }

    /**
     * Pre-commit phase throws on any validation failure — callers translate
     * that into a hard navigation (click) or reload (popstate). Nothing in the
     * live DOM is touched until the incoming document has been validated.
     * Post-commit phase is fully isolated: no integration can kill navigation.
     */
    function commitNavigation(html, historyPath, opts) {
      var parser = new win.DOMParser();
      var parsedDoc = parser.parseFromString(html, 'text/html');
      var newContent = parsedDoc.querySelector('#content > div');
      var titleEl = parsedDoc.querySelector('title');
      if (!newContent || !titleEl) throw new Error('document missing #content or title');
      var liveTarget = doc.querySelector('#content > div');
      if (!liveTarget) throw new Error('live document missing #content');

      var previousUrl = currentVirtualUrl;

      liveTarget.replaceWith(newContent);
      if (opts.push && historyPath) {
        var currentState = win.history.state && win.history.state.path;
        if (currentState !== historyPath) {
          win.history.pushState({ path: historyPath }, '', historyPath);
        }
      }
      doc.title = titleEl.textContent || titleEl.innerText || doc.title;
      currentVirtualUrl = win.location.href;

      runPostCommit(previousUrl);
    }

    // ---- swapped-in section scripts --------------------------------------
    function scriptUrl(el) {
      // .src is the resolved absolute URL in browsers; fall back to the raw
      // attribute for environments without URL resolution (test harness).
      return el.src || (typeof el.getAttribute === 'function' && el.getAttribute('src')) || null;
    }

    function readAttr(el, name) {
      if (typeof el.getAttribute === 'function') return el.getAttribute(name);
      var attrs = el.attributes;
      if (attrs) {
        for (var i = 0; i < attrs.length; i++) {
          if (attrs[i].name === name) return attrs[i].value;
        }
      }
      return null;
    }

    var supportsModulesCache = null;
    function supportsModules() {
      if (supportsModulesCache === null) {
        try {
          supportsModulesCache = 'noModule' in doc.createElement('script');
        } catch (err) {
          supportsModulesCache = false;
        }
      }
      return supportsModulesCache;
    }

    // The JavaScript MIME essence list from the HTML spec's prepare-a-script
    // steps — a type outside this set (and not "module") makes the element a
    // data block the browser never fetches or runs.
    var JS_MIME_TYPES = {
      'application/ecmascript': 1,
      'application/javascript': 1,
      'application/x-ecmascript': 1,
      'application/x-javascript': 1,
      'text/ecmascript': 1,
      'text/javascript': 1,
      'text/javascript1.0': 1,
      'text/javascript1.1': 1,
      'text/javascript1.2': 1,
      'text/javascript1.3': 1,
      'text/javascript1.4': 1,
      'text/javascript1.5': 1,
      'text/jscript': 1,
      'text/livescript': 1,
      'text/x-ecmascript': 1,
      'text/x-javascript': 1
    };

    // The spec strips ASCII whitespace only — \s would also strip NBSP and
    // friends, accepting a type a hard load rejects (audit 006 blocker).
    function asciiTrimLower(value) {
      return String(value).replace(/^[ \t\n\f\r]+|[ \t\n\f\r]+$/g, '').toLowerCase();
    }

    var hasOwnProp = Object.prototype.hasOwnProperty;

    /**
     * Mirrors the prepare-a-script execute-or-skip decision for every shape
     * that is decidable statically (audit 004 + 006 blockers), per
     * https://html.spec.whatwg.org/multipage/scripting.html#prepare-the-script-element:
     *  - type string: empty type attr (or neither type nor language) means
     *    text/javascript; a present type is ASCII-trimmed; otherwise a
     *    non-empty legacy `language` attr yields "text/" + language.
     *  - classic requires a JavaScript MIME essence match — membership via
     *    hasOwnProperty so prototype keys ("constructor", "__proto__") stay
     *    data blocks; "module" matches ASCII-case-insensitively.
     *  - nomodule skips CLASSIC scripts on module-supporting browsers only.
     *  - legacy event/for on a classic script executes only for
     *    for="window" with event "onload" or "onload()" (ASCII-trimmed,
     *    case-insensitive).
     * Only elements that WOULD execute may be recorded in executedScriptSrcs
     * or cloned — a browser-skipped shape must not poison the URL for a later
     * executable occurrence that a hard load would run.
     */
    function wouldExecute(el) {
      // A present-but-empty src content attribute never fetches on a hard
      // load (the external-source step returns on empty src), but the .src
      // IDL reflection resolves "" to the document URL — reject on the RAW
      // attribute before anything resolves it (audit 008 blocker).
      if (readAttr(el, 'src') === '') return false;
      var typeAttr = readAttr(el, 'type');
      var langAttr = readAttr(el, 'language');
      var typeString;
      if (typeAttr === '' || (typeAttr == null && (langAttr == null || langAttr === ''))) {
        typeString = 'text/javascript';
      } else if (typeAttr != null) {
        typeString = asciiTrimLower(typeAttr);
      } else {
        // Spec concatenates without trimming the language value.
        typeString = 'text/' + String(langAttr).toLowerCase();
      }
      var isModule = typeString === 'module';
      var isClassic = hasOwnProp.call(JS_MIME_TYPES, typeString);
      if (!isModule && !isClassic) return false;
      if (isClassic) {
        if (readAttr(el, 'nomodule') !== null && supportsModules()) return false;
        var eventAttr = readAttr(el, 'event');
        var forAttr = readAttr(el, 'for');
        if (eventAttr != null && forAttr != null) {
          if (asciiTrimLower(forAttr) !== 'window') return false;
          var eventValue = asciiTrimLower(eventAttr);
          if (eventValue !== 'onload' && eventValue !== 'onload()') return false;
        }
      }
      return true;
    }

    // Called once at boot: every EXECUTABLE script[src] present at
    // DOMContentLoaded has been (or will be) executed by the browser's own
    // loader. Browser-skipped shapes are deliberately left unrecorded, and
    // the predicate runs BEFORE scriptUrl so a rejected element is never
    // resolved (audit 008).
    function recordExecutedScripts() {
      var scripts = doc.querySelectorAll('script[src]');
      for (var i = 0; i < scripts.length; i++) {
        if (!wouldExecute(scripts[i])) continue;
        var src = scriptUrl(scripts[i]);
        if (src) executedScriptSrcs[src] = true;
      }
    }

    /**
     * Sections render their own deferred `<script src>` tags inside #content
     * (scrolling-banner.js, countdown-timer.js, ...). After a swap those tags
     * are inert — DOMParser's non-executable flag is permanent — so a section
     * the origin page never loaded would arrive as dead markup. Re-create each
     * not-yet-executed URL as a live script so the browser fetches and runs it;
     * custom-element definitions then upgrade the already-inserted markup.
     * Appended to <head> so the tag survives later content swaps; deduped via
     * executedScriptSrcs so this pass never runs a URL twice per pageview.
     * Contract limit (audit 002, accepted residual): the set tracks boot-time
     * scripts and this pass's own injections. A URL that a third party
     * injects AFTER boot and that also appears inside fetched content could
     * run twice. The five audited section scripts (scrolling-banner,
     * variant-picker, sticky-atc, video, countdown-timer) guard their
     * definitions; other section assets are NOT all guarded (audit 006), so
     * do not widen that claim without checking the file.
     */
    function runSwappedInSectionScripts() {
      var contentRoot = doc.querySelector('#content');
      if (!contentRoot || typeof contentRoot.querySelectorAll !== 'function') return;
      var scripts = contentRoot.querySelectorAll('script[src]');
      for (var i = 0; i < scripts.length; i++) {
        var el = scripts[i];
        // Browser-skipped shapes (data blocks, nomodule on a modern browser,
        // empty raw src) are neither resolved, recorded, nor cloned: the
        // inert original already gives DOM-presence parity, and leaving the
        // URL unrecorded lets a later EXECUTABLE occurrence of the same URL
        // run, as a hard load would (audit 004 + 008 blockers).
        if (!wouldExecute(el)) continue;
        var src = scriptUrl(el);
        if (!src || executedScriptSrcs[src]) continue;
        executedScriptSrcs[src] = true;
        var runnable = doc.createElement('script');
        // Hard-load parity (audit 002 blocker): copy every authored attribute
        // — type, integrity, crossorigin, referrerpolicy, nonce — so the
        // clone fetches and executes under the same constraints the original
        // would have on a hard load. App blocks render script shapes this
        // repo does not control. `id` is deliberately NOT copied: identity
        // and anchor semantics stay with the authored element in #content;
        // duplicating the ID onto a head clone would shadow it in tree order
        // (audit 004 finding).
        var attrs = el.attributes;
        if (attrs && attrs.length) {
          for (var a = 0; a < attrs.length; a++) {
            if (attrs[a].name === 'id') continue;
            runnable.setAttribute(attrs[a].name, attrs[a].value);
          }
        }
        // Chrome hides an executed nonce from the content attribute; the IDL
        // property still carries it across when present.
        if (el.nonce && !runnable.nonce) runnable.nonce = el.nonce;
        runnable.src = src;
        // Dynamically-inserted scripts ignore `defer`; async=false keeps the
        // execution order of the scripts injected in this pass (it also
        // clears any copied async attribute).
        runnable.async = false;
        (doc.head || doc.documentElement).appendChild(runnable);
        debug('running swapped-in section script: ' + src);
      }
    }

    function runPostCommit(previousUrl) {
      safeRun('run swapped-in section scripts', runSwappedInSectionScripts);
      safeRun('close mobile nav', function () {
        doc.body.classList.remove('enable-mobile-nav-transition', 'reveal-mobile-nav', 'reveal-mobile-nav--revealed');
      });
      safeRun('close modals', function () {
        var modals = doc.querySelectorAll('modal-dialog');
        for (var i = 0; i < modals.length; i++) {
          if (typeof modals[i].close === 'function') modals[i].close();
        }
      });
      safeRun('reset variant pills', function () {
        var variantContents = doc.querySelectorAll('variant-content.product-label-list');
        for (var i = 0; i < variantContents.length; i++) delete variantContents[i].dataset.pillsInitialized;
        if (typeof win.initializeProductLabelPills === 'function') win.initializeProductLabelPills();
      });
      safeRun('reset gallery dots', function () {
        var sliders = doc.querySelectorAll('.mobile-gallery--dots .slider');
        for (var i = 0; i < sliders.length; i++) delete sliders[i].dataset.dotsInitialized;
        if (typeof win.initMobileGalleryDots === 'function') {
          win.setTimeout(function () {
            safeRun('gallery dots delayed init', function () { win.initMobileGalleryDots(); });
          }, 100);
        }
      });
      safeRun('klaviyo atc listeners', function () {
        if (typeof win.attachKlaviyoAddToCartListeners === 'function') win.attachKlaviyoAddToCartListeners();
      });
      safeRun('soft-nav tracking', function () {
        trackSoftNav(previousUrl);
      });
      safeRun('short reviews', function () { runShortReviewScript(); });
      safeRun('review slider', function () { runReviewSliderScript(); });
      safeRun('spend and save message', function () {
        if (typeof win.updatePDPRewardMessage === 'function') win.updatePDPRewardMessage();
      });
      safeRun('free gift message', function () {
        if (typeof win.updatePDPFreeGiftMessage === 'function') win.updatePDPFreeGiftMessage();
      });
      safeRun('star rating handler', function () {
        if (typeof win.initStarRatingClickHandler === 'function') win.initStarRatingClickHandler();
      });
      safeRun('view item events', function () {
        sendViewItemEvent(previousUrl);
      });
      safeRun('okendo widgets', function () {
        if (win.okeWidgetApi && typeof win.okeWidgetApi.initAllWidgets === 'function') win.okeWidgetApi.initAllWidgets();
      });
      safeRun('scroll to top', function () { win.scrollTo(0, 0); });
      safeRun('play background video', function () { playBackgroundVideo(); });
      // New links in the swapped content are registered by the #content
      // MutationObserver; no explicit re-registration pass is needed here.
    }

    // ---- tracking --------------------------------------------------------
    /**
     * Exactly-once page-level tracking per committed soft navigation.
     * Direct per-vendor calls by design — there is no GTM container.
     * Shopify built-in reports cannot be reached from theme JS (see header).
     */
    function trackSoftNav(previousUrl) {
      if (!config.softNavTracking) return;
      var absoluteUrl = win.location.href;
      safeRun('ga4 page_view', function () {
        if (typeof win.gtag === 'function') {
          win.gtag('event', 'page_view', {
            page_location: absoluteUrl,
            page_title: doc.title,
            page_referrer: previousUrl
          });
        }
      });
      safeRun('meta PageView', function () {
        if (typeof win.fbq === 'function') win.fbq('track', 'PageView');
      });
      safeRun('hotjar stateChange', function () {
        if (typeof win.hj === 'function') win.hj('stateChange', win.location.pathname + win.location.search);
      });
      safeRun('shopify custom pixel event', function () {
        if (win.Shopify && win.Shopify.analytics && typeof win.Shopify.analytics.publish === 'function') {
          win.Shopify.analytics.publish('darmha:soft_page_view', {
            url: absoluteUrl,
            title: doc.title,
            referrer: previousUrl
          });
        }
      });
    }

    function getProductData() {
      var productDataScript = doc.getElementById('product-data');
      if (!productDataScript) return null;
      var productData = JSON.parse(productDataScript.textContent);
      // Shopify.currency.active is preferred because it follows a soft
      // navigation, but it is not always present. Fall back to the island's
      // own server-rendered presentment currency rather than overwriting a
      // correct value with undefined, which would send unlabelled amounts.
      var activeCurrency = win.Shopify && win.Shopify.currency && win.Shopify.currency.active;
      productData.currency = activeCurrency || productData.currency;
      return productData;
    }

    function formatPrice(price, currency) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(price);
    }

    function sendViewItemEvent(previousUrl) {
      var productData = getProductData();
      if (!productData) return;

      var item = {
        Name: productData.item_name,
        ProductID: productData.item_id,
        Categories: [productData.categories],
        ImageURL: (productData.imageUrl || '').replace(/\\u0026/g, '&'),
        URL: win.location.href,
        Brand: productData.item_brand,
        Price: formatPrice(productData.price, productData.currency),
        CompareAtPrice: productData.compare_at_price
          ? formatPrice(productData.compare_at_price, productData.currency)
          : formatPrice(0, productData.currency)
      };

      safeRun('klaviyo viewed product', function () {
        var learnq = win._learnq = win._learnq || [];
        learnq.push(['track', 'Viewed Product', item]);
        learnq.push(['trackViewedItem', {
          Title: item.Name,
          ItemId: item.ProductID,
          Categories: item.Categories,
          ImageUrl: item.ImageURL,
          Url: item.URL,
          Metadata: {
            Brand: item.Brand,
            Price: item.Price,
            CompareAtPrice: item.CompareAtPrice
          }
        }]);
      });

      safeRun('ga4 view_item', function () {
        if (typeof win.gtag !== 'function') return;
        win.gtag('event', 'view_item', {
          items: [{
            id: 'shopify_AU_' + productData.item_id + '_' + productData.variant_id,
            name: productData.item_name,
            currency: productData.currency,
            price: productData.price,
            brand: productData.item_brand,
            category: productData.item_category,
            variant: productData.item_variant
          }],
          ecomm_prodid: 'shopify_AU_' + productData.item_id + '_' + productData.variant_id,
          ecomm_pagetype: 'product',
          page_location: win.location.href,
          page_referrer: previousUrl
        });
      });

      safeRun('instant product viewed', function () {
        if (!win.InstantJS || typeof win.InstantJS.track !== 'function') return;
        win.InstantJS.track('PRODUCT_VIEWED', [{
          provider: 'KLAVIYO',
          event: {
            ProductName: productData.item_name,
            ProductID: productData.item_id,
            Categories: [productData.item_category],
            URL: win.location.href,
            Brand: productData.item_brand,
            Price: productData.price,
            CompareAtPrice: productData.compare_at_price || productData.price,
            ImageURL: (productData.imageUrl || '').replace(/\\u0026/g, '&')
          }
        }]);
      });

      safeRun('meta ViewContent', function () {
        if (typeof win.fbq !== 'function') return;
        var contentType = (productData.variantCount && productData.variantCount > 1) ? 'product_group' : 'product';
        win.fbq('track', 'ViewContent', {
          content_ids: [productData.item_id],
          content_name: productData.item_full_name,
          content_category: productData.item_category,
          content_type: contentType,
          value: productData.price,
          currency: productData.currency
        });
      });
    }

    // ---- legacy re-init helpers (unchanged behaviour, quieter logging) ----
    function runShortReviewScript() {
      var shortReview = doc.querySelector('.short-review');
      if (shortReview) {
        var reviewText = shortReview.querySelector('.review-text');
        var moreLink = shortReview.querySelector('.more-link');
        if (reviewText && moreLink) {
          var fullText = reviewText.textContent.trim().replace(/^"(.*)"$/, '$1');
          var words = fullText.split(' ');
          if (words.length > 20) {
            reviewText.textContent = words.slice(0, 20).join(' ');
            reviewText.classList.add('truncated');
            shortReview.classList.add('truncated');
            moreLink.addEventListener('click', function (e) {
              e.preventDefault();
              reviewText.textContent = fullText;
              reviewText.classList.remove('truncated');
              moreLink.style.display = 'none';
            });
          }
        }
      }
      if (typeof win.initializeMobileReviews === 'function') win.initializeMobileReviews();
    }

    function runReviewSliderScript() {
      // Every stage inside these timers is individually guarded: timer
      // callbacks run outside the runPostCommit safeRun, so an unguarded
      // throw here would skip later initializers (005 finding 1).
      win.setTimeout(function () {
        safeRun('review slider reset', function () {
          var container = doc.querySelector('[data-review-slider]');
          if (!container) return;
          var existingInterval = container.dataset.intervalId;
          var existingTimeout = container.dataset.timeoutId;
          if (existingInterval) win.clearInterval(parseInt(existingInterval, 10));
          if (existingTimeout) win.clearTimeout(parseInt(existingTimeout, 10));
          container.dataset.initialized = 'false';
        });
        var callInit = function (name) {
          if (typeof win[name] === 'function') {
            safeRun(name, function () { win[name](); });
          } else {
            win.setTimeout(function () {
              if (typeof win[name] === 'function') {
                safeRun(name + ' (retry)', function () { win[name](); });
              }
            }, 100);
          }
        };
        callInit('initReviewSlider');
        callInit('initializeMobileReviews');
      }, 50);
    }

    function playBackgroundVideo() {
      var videoComponents = doc.querySelectorAll('video-component[data-autoplay="true"][data-background="true"]');
      for (var i = 0; i < videoComponents.length; i++) {
        var videoElement = videoComponents[i].querySelector('video');
        if (videoElement && typeof videoElement.play === 'function') {
          videoElement.play().catch(function (err) { log('background video play failed', err); });
        }
      }
    }

    // ---- acquisition -----------------------------------------------------
    function createObserver() {
      if (typeof win.IntersectionObserver !== 'function') return null;
      return new win.IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          var link = entries[i].target;
          io.unobserve(link);
          if (schedulePrefetch(link.href, false) === false) {
            // Rejected by the current mode only. The observer has already
            // released the link and will not fire again while it stays
            // visible, so remember it for the connection-change wake-up.
            // Deduped and bounded: oldest entries drop first (014 finding).
            if (deferredLinks.indexOf(link) === -1) {
              deferredLinks.push(link);
              if (deferredLinks.length > MAX_DEFERRED_LINKS) deferredLinks.shift();
            }
          }
        }
      }, { rootMargin: VIEWPORT_LOOKAHEAD });
    }

    function registerLinksWithin(root) {
      if (!root || typeof root.querySelectorAll !== 'function') return;
      if (!acquisitionStarted) {
        pendingRoots.push(root);
        return;
      }
      if (!io || !registeredLinks) return;
      var links = root.querySelectorAll('a.product-link[href*="/products/"]');
      var i;
      // querySelectorAll only matches descendants — when the added node IS
      // the anchor (e.g. a single link appended to a grid), include it too.
      if (typeof root.matches === 'function' && root.matches('a.product-link[href*="/products/"]')) {
        links = Array.prototype.slice.call(links);
        links.push(root);
      }
      for (i = 0; i < links.length; i++) {
        var link = links[i];
        if (registeredLinks.has(link)) continue;
        if (link.classList && link.classList.contains('quickbuy-toggle')) continue;
        registeredLinks.add(link);
        io.observe(link);
      }
    }

    function onIntentEvent(event) {
      var target = event.target;
      var anchor = target && typeof target.closest === 'function' ? target.closest('a.product-link') : null;
      if (!anchor || !anchor.href) return;
      if (anchor.classList && anchor.classList.contains('quickbuy-toggle')) return;
      schedulePrefetch(anchor.href, true);
    }

    function startAcquisition() {
      if (acquisitionStarted) return;
      if (doc.visibilityState === 'hidden') {
        // Singleton wait: load+idle and the cap timer can both land here
        // while hidden — only one visibilitychange listener may exist
        // (012 finding 2).
        if (waitingForVisibility) return;
        waitingForVisibility = true;
        doc.addEventListener('visibilitychange', function onVisible() {
          if (doc.visibilityState !== 'hidden') {
            doc.removeEventListener('visibilitychange', onVisible);
            waitingForVisibility = false;
            startAcquisition();
          }
        });
        return;
      }
      acquisitionStarted = true;
      io = createObserver();
      debug('acquisition started');
      registerLinksWithin(doc);
      var roots = pendingRoots.splice(0, pendingRoots.length);
      for (var i = 0; i < roots.length; i++) registerLinksWithin(roots[i]);
    }

    function scheduleAcquisitionStart() {
      var begin = function () {
        if (typeof win.requestIdleCallback === 'function') {
          win.requestIdleCallback(function () { startAcquisition(); }, { timeout: 2000 });
        } else {
          win.setTimeout(function () { startAcquisition(); }, 200);
        }
      };
      if (doc.readyState === 'complete') begin();
      else win.addEventListener('load', begin, { once: true });
      win.setTimeout(function () { startAcquisition(); }, PROXIMITY_START_CAP_MS);
    }

    function installContentObserver() {
      if (typeof win.MutationObserver !== 'function') return;
      var contentRoot = doc.querySelector('#content');
      if (!contentRoot) return;
      var observer = new win.MutationObserver(function (mutations) {
        var sawAdditions = false;
        var touchedAjaxContainer = false;
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length > 0) {
            sawAdditions = true;
            var mutationTarget = mutations[i].target;
            if (mutationTarget && typeof mutationTarget.closest === 'function'
              && mutationTarget.closest('div[data-ajax-container]')) {
              touchedAjaxContainer = true;
            }
            for (var j = 0; j < mutations[i].addedNodes.length; j++) {
              var addedNode = mutations[i].addedNodes[j];
              if (addedNode && addedNode.nodeType === 1) registerLinksWithin(addedNode);
            }
          }
        }
        // Filtered collection results replace the grid; refresh the snapshot
        // so back-navigation restores the filtered state (v1 parity).
        if (sawAdditions && touchedAjaxContainer) {
          safeRun('snapshot after filter change', snapshotCurrentPage);
        }
      });
      observer.observe(contentRoot, { childList: true, subtree: true });
    }

    // ---- boot ------------------------------------------------------------
    function boot() {
      safeRun('record executed scripts', recordExecutedScripts);
      safeRun('initial page snapshot', snapshotCurrentPage);
      doc.addEventListener('click', onDocumentClick);
      win.addEventListener('popstate', onPopstate);
      // mouseenter does not bubble; mouseover does and closest() scopes it.
      doc.addEventListener('mouseover', onIntentEvent, { passive: true });
      doc.addEventListener('touchstart', onIntentEvent, { passive: true });
      doc.addEventListener('focusin', onIntentEvent);
      var conn = win.navigator && win.navigator.connection;
      if (conn && typeof conn.addEventListener === 'function') {
        conn.addEventListener('change', onConnectionChange);
      }
      installContentObserver();
      scheduleAcquisitionStart();
      debug('booted, mode: ' + acquisitionMode() + ', ttl minutes: ' + (cacheTtlMs() / 60000));
    }

    return {
      boot: boot,
      registerLinksWithin: registerLinksWithin,
      cacheStore: cacheStore,
      config: config,
      _internals: {
        config: config,
        cacheStore: cacheStore,
        cacheKeyOrder: cacheKeyOrder,
        registry: registry,
        queue: queue,
        inflight: function () { return inflightCount; },
        canonicalize: canonicalize,
        getPathWithoutQuery: getPathWithoutQuery,
        getPrimaryUrl: getPrimaryUrl,
        isProductPath: isProductPath,
        acquisitionMode: acquisitionMode,
        currentConcurrencyCap: currentConcurrencyCap,
        cacheWrite: cacheWrite,
        readFresh: readFresh,
        snapshotCurrentPage: snapshotCurrentPage,
        schedulePrefetch: schedulePrefetch,
        onConnectionChange: onConnectionChange,
        deferredLinks: deferredLinks,
        awaitDocument: awaitDocument,
        softNavigate: softNavigate,
        onDocumentClick: onDocumentClick,
        onPopstate: onPopstate,
        onIntentEvent: onIntentEvent,
        commitNavigation: commitNavigation,
        executedScriptSrcs: executedScriptSrcs,
        recordExecutedScripts: recordExecutedScripts,
        runSwappedInSectionScripts: runSwappedInSectionScripts,
        trackSoftNav: trackSoftNav,
        sendViewItemEvent: sendViewItemEvent,
        startAcquisition: startAcquisition,
        isEligibleClick: isEligibleClick
      }
    };
  }

  // Test seam and preview-panel integration point.
  if (typeof window !== 'undefined') {
    window.PrefetchAccel = window.PrefetchAccel || {};
    window.PrefetchAccel.create = createAccelerator;

    // Defense-in-depth: never run inside the Shopify theme editor (see header).
    if (window.Shopify && window.Shopify.designMode) return;

    document.addEventListener('DOMContentLoaded', function () {
      var accel = createAccelerator({
        win: window,
        doc: document,
        fetchFn: function (url) { return window.fetch(url); },
        hardNavigate: function (url) { window.location.href = url; },
        reloadPage: function () { window.location.reload(); }
      });
      // Object identity is part of the compatibility contract:
      // product-preview-panel.js reads window.prefetchCache directly.
      window.prefetchCache = accel.cacheStore;
      window.PrefetchAccel.instance = accel;
      accel.boot();
    });
  }
})();
